import { Box, Typography, Button, Divider, Stack, FormControl, useTheme } from "@mui/material";
import { ActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetOneActionPlanAction";
import { useTranslation } from "react-i18next";
import {
    ActionPlanActionWrite,
    useUpsertActionPlanAction,
} from "src/api/client-api/ActionPlan/ActionPlanAction/useUpsertActionPlanAction";
import { getActionStatus } from "src/components/Actions/Common/actionStatus";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { FoodsIcon, FoodsSelect, FoodsSelectServerPagination } from "@foodpilot/foods";
import { useNavigate } from "react-router";
import { useRecalculateActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useRecalculateActionPlan";
import { useDeleteActionPlanAction } from "src/api/client-api/ActionPlan/ActionPlanAction/useDeleteActionPlanAction";
import { useAllUsers } from "src/api/client-api/user/useAllUsers";
import { useDelayedSearch } from "src/utils/useDelayedSearch";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { APScore } from "src/context/Actions/useActionPlanData";

type Props = {
    actionPlanAction: ActionPlanAction;
};

export const ActionConfiguration = (props: Props) => {
    const { actionPlanAction } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const { lastSelectedVariation } = useEntityScores();
    const { actionPlan, savingStatus, actionPlanData, selectPostsByActionPlanAction, urls } = useActionsContext();
    const { selectedCompany } = useFoodpilotContext();
    const upsertActionPlanAction = useUpsertActionPlanAction({ actionPlanID: actionPlan?.id });

    const recalculate = useRecalculateActionPlan(actionPlan?.id);
    const deleteActionPlanAction = useDeleteActionPlanAction();

    const { applicationParameters } = useApplicationParameters();
    const isResponsibleSelectionAllowed = applicationParameters?.["actionPlan-allowResponsibleSelection"] === "true";

    const allItems = getActionStatus(t);
    const existingType = actionPlanAction.type;
    const existingStatus =
        existingType === " " ? allItems[0] : allItems.find((item) => item.backendLabel === existingType);
    const companyId = selectedCompany !== null ? selectedCompany.id : undefined;

    // Logic for user search
    const { search: userSearch, delayedSearch: delayedUserSearch, setSearch: setUserSearch } = useDelayedSearch();
    const { users } = useAllUsers({
        search: delayedUserSearch,
        itemsPerPage: 50,
        companyId,
        enabled: isResponsibleSelectionAllowed,
    });
    const isUserSearchFound = (value: string) => {
        if (delayedUserSearch === "") {
            return true;
        }
        return value.toLowerCase().includes(delayedUserSearch.toLowerCase());
    };

    // Extract user ID from IRI (format: "/api/users/123")
    const extractUserIdFromIRI = (iri: string): number | null => {
        const match = iri.match(/\/api\/users\/(\d+)$/);
        return match ? parseInt(match[1]) : null;
    };

    // Find the selected user in the users list to ensure proper display
    const selectedUser =
        actionPlanAction.assignee ?
            users?.find((user) => {
                const assigneeId = extractUserIdFromIRI(actionPlanAction.assignee!);
                return assigneeId && user.id === assigneeId;
            }) || null
        :   null;

    // Logique pour les dates (reprise de Timerange.tsx)
    const _actionPlanData = actionPlanData.find(
        (ap) => ap.scoreId === lastSelectedVariation.scoreId && ap.variationId === lastSelectedVariation.variationId,
    );
    const { start, target, posts } =
        _actionPlanData ?
            selectPostsByActionPlanAction(_actionPlanData, actionPlanAction)
        :   { start: { post: null }, target: { post: null }, posts: [] as APScore[] };

    const formatToWrite = (value: ActionPlanAction): ActionPlanActionWrite => {
        const makeIRI = (value: number) => {
            return `/api/ssq-posts/${value}`;
        };

        return {
            id: value.id,
            actionId: value.actionId,
            actionPlan: value.actionPlan,
            startSimulationSsqPost: makeIRI(value.startSimulationSsqPost.id),
            endSimulationSsqPost: makeIRI(value.endSimulationSsqPost.id),
            type: value.type,
        };
    };

    // Fonction utilitaire pour obtenir la couleur d'un statut (basée sur StatusTag.tsx)
    const getStatusColor = (backendLabel: string | undefined) => {
        // Si pas de statut défini, retourner gris
        if (!backendLabel || backendLabel.trim() === "" || backendLabel === "no-status" || backendLabel === " ") {
            return "#9CA3AF"; // Couleur grise
        }

        switch (backendLabel?.toLowerCase()) {
            case "en cours":
                return "#5268FF";
            case "terminée":
            case "terminé":
                return "#12A332";
            case "planifiée":
            case "planifié":
                return "#FFB400";
            case "en réflexion":
                return "#FF7D60";
            case "intégrée":
            case "intégré":
                return "#B249C6";
            default:
                return "#9CA3AF"; // Gris par défaut pour les statuts non reconnus
        }
    };

    const endPosts = posts.filter((post) => post.id !== _actionPlanData?.metadata.startingPost.id);
    const selectedStartingPost = posts.find((post) => post.id === start.post?.id);
    const selectedEndPost = endPosts.find((post) => post.id === target.post?.id);

    return (
        <Box
            sx={{
                width: "100%",
                maxWidth: {
                    xs: "100%",
                    // md: "257px",
                },
                backgroundColor: "#FFFFFF",
                border: "1px solid #E7E6E4",
                borderRadius: "12px",
                padding: "24px",
                boxShadow: "0px 2px 8px 0px rgba(42, 43, 39, 0.04)",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
            }}
        >
            {/* Section dates */}
            <Stack spacing={2}>
                {/* Date de début */}
                <Stack gap={theme.spacing(1)}>
                    <Typography variant="small-bold">
                        {t("action_plan.start_of_implementation", "Start of implementation")}
                    </Typography>
                    <FoodsSelect
                        selectedOption={selectedStartingPost ?? null}
                        options={posts}
                        onChange={(selectedValue) => {
                            if (selectedValue === null) {
                                return;
                            }

                            savingStatus.setSaving(true);
                            const newActionPlanAction: ActionPlanAction = {
                                ...actionPlanAction,
                                startSimulationSsqPost: selectedValue,
                            };
                            const newData = formatToWrite(newActionPlanAction);
                            upsertActionPlanAction.mutate(newData, {
                                onSettled: () => {
                                    savingStatus.setSaving(false);
                                },
                            });
                        }}
                        getId={(item) => {
                            return item.id;
                        }}
                        getName={(item) => {
                            const label = item.campaign?.label;
                            if (label === undefined) return "unknown campaign";

                            // Specific request from Product Team.
                            // We know the Starting Date is N, but we display N-1.
                            // It's supposed to help the customer understanding.
                            const adjustedYear = parseInt(label) - 1;
                            return adjustedYear.toString();
                        }}
                        disabled={savingStatus.isSaving}
                        disableSearch
                        textOptions={{
                            placeholder: "",
                            searchPlaceholder: "",
                            emptyOptionsText: "",
                            defaultValueText: undefined,
                            noResult: "",
                        }}
                        BoxProps={{
                            width: "auto",
                        }}
                    />
                </Stack>

                {/* Date de fin */}
                <Stack gap={theme.spacing(1)}>
                    <Typography variant="small-bold">
                        {t("action_plan.end_of_implementation", "End of implementation")}
                    </Typography>
                    <FoodsSelect
                        selectedOption={selectedEndPost ?? null}
                        options={endPosts}
                        onChange={(selectedValue) => {
                            if (selectedValue === null) {
                                return;
                            }

                            savingStatus.setSaving(true);
                            const newActionPlanAction: ActionPlanAction = {
                                ...actionPlanAction,
                                endSimulationSsqPost: selectedValue,
                            };
                            const newData = formatToWrite(newActionPlanAction);
                            upsertActionPlanAction.mutate(newData, {
                                onSettled: () => {
                                    savingStatus.setSaving(false);
                                },
                            });
                        }}
                        getId={(item) => {
                            return item.id;
                        }}
                        getName={(item) => {
                            return item.campaign?.label ?? "unknown campaign";
                        }}
                        disabled={savingStatus.isSaving}
                        disableSearch
                        textOptions={{
                            placeholder: "",
                            searchPlaceholder: "",
                            emptyOptionsText: "",
                            defaultValueText: undefined,
                            noResult: "",
                        }}
                        BoxProps={{
                            width: "auto",
                        }}
                    />
                </Stack>
            </Stack>

            <Divider sx={{ borderColor: "#E7E6E4" }} />

            {isResponsibleSelectionAllowed && (
                <Box>
                    <Typography
                        variant="small-bold"
                        sx={{
                            marginBottom: "8px",
                        }}
                    >
                        {t("action_plan.responsible", "Responsible")}
                    </Typography>
                    <FormControl fullWidth size="small">
                        <FoodsSelectServerPagination
                            selectedOption={selectedUser}
                            options={users || []}
                            onChange={(selectedUser) => {
                                savingStatus.setSaving(true);
                                const newActionPlanAction: ActionPlanActionWrite = {
                                    id: actionPlanAction.id,
                                    assignee: selectedUser ? selectedUser["@id"] : undefined,
                                };
                                upsertActionPlanAction.mutate(newActionPlanAction, {
                                    onSettled: () => {
                                        savingStatus.setSaving(false);
                                    },
                                });
                            }}
                            getId={(user) => user.id}
                            getName={(user) => `${user.firstname} ${user.lastname} (${user.email})`}
                            disabled={savingStatus.isSaving}
                            search={userSearch}
                            setSearch={setUserSearch}
                            isSearchFound={isUserSearchFound}
                            textOptions={{
                                placeholder: t("action_plan.select_responsible", "Select responsible"),
                                searchPlaceholder: t("action_plan.search_responsible", "Search by name or email..."),
                                emptyOptionsText: t("action_plan.no_user_available", "No user available"),
                                defaultValueText: t("action_plan.no_responsible", "No responsible"),
                                noResult: t("action_plan.no_user_found", "No user found"),
                            }}
                            noValue={t("action_plan.no_responsible", "No responsible")}
                            BoxProps={{
                                width: "100%", // S'adapte au conteneur parent
                                sx: {
                                    backgroundColor: "#FFFFFF",
                                    border: "1px solid #E7E6E4",
                                    borderRadius: "24px",
                                    height: "40px",
                                    "& .MuiOutlinedInput-notchedOutline": {
                                        border: "none",
                                    },
                                },
                            }}
                            BoxOptionsProps={{
                                width: {
                                    xs: "100%",
                                    md: "257px",
                                },
                            }}
                        />
                    </FormControl>
                </Box>
            )}

            {/* Section Statut */}
            <Stack gap={theme.spacing(1)}>
                <Typography variant="small-bold">{t("action_plan.status", "Status")}</Typography>
                <FoodsSelect
                    selectedOption={existingStatus ?? null}
                    options={allItems}
                    onChange={(selectedValue) => {
                        if (selectedValue === null) {
                            return;
                        }

                        savingStatus.setSaving(true);
                        const newActionPlanAction: ActionPlanActionWrite = {
                            id: actionPlanAction.id,
                            type: selectedValue.backendLabel,
                        };
                        upsertActionPlanAction.mutate(newActionPlanAction, {
                            onSettled: () => {
                                savingStatus.setSaving(false);
                            },
                        });
                    }}
                    getId={(item) => {
                        return item.id;
                    }}
                    getName={(item) => {
                        return item.label;
                    }}
                    getIcon={(item) => {
                        return (
                            <Box
                                sx={{
                                    minWidth: "16px",
                                    minHeight: "16px",
                                    backgroundColor: getStatusColor(item.backendLabel),
                                    borderRadius: "12px",
                                }}
                            />
                        );
                    }}
                    disabled={savingStatus.isSaving}
                    disableSearch
                    textOptions={{
                        placeholder: "",
                        searchPlaceholder: "",
                        emptyOptionsText: "",
                        defaultValueText: undefined,
                        noResult: "",
                    }}
                    BoxProps={{
                        width: "auto",
                    }}
                />
            </Stack>

            {/* Ligne de séparation */}
            <Divider sx={{ borderColor: "#E7E6E4" }} />

            {/* Section Boutons */}
            <Stack spacing={2} alignItems="center">
                {/* Bouton Valider */}
                <Button
                    variant="validate"
                    onClick={() => {
                        savingStatus.setSaving(true);

                        if (
                            Array.isArray(actionPlanAction.action.actionFields) &&
                            actionPlanAction.action.actionFields.length === 0
                        ) {
                            savingStatus.setSaving(false);
                            const url = urls.planning();
                            navigate(url);

                            return;
                        }

                        recalculate.mutate(null, {
                            onSuccess: () => {
                                savingStatus.setSaving(false);
                                const url = urls.planning();
                                navigate(url);
                            },
                            onSettled: () => {
                                savingStatus.setSaving(false);
                            },
                        });
                    }}
                    sx={{ width: "100%" }}
                    startIcon={<FoodsIcon size={2} icon={"check"} />}
                >
                    <Typography variant="small-bold" color="white">
                        {t("action_plan.validate_action", "Validate action")}
                    </Typography>
                </Button>

                {/* Bouton Supprimer */}
                <Button
                    variant="error"
                    onClick={() => {
                        savingStatus.setSaving(true);
                        const payload = {
                            actionPlanActionId: actionPlanAction.id,
                            actionPlanId: actionPlan?.id,
                        };
                        deleteActionPlanAction.mutate(payload, {
                            onSuccess: () => {
                                savingStatus.setSaving(false);
                                const url = urls.planning();
                                navigate(url);
                            },
                            onSettled: () => {
                                savingStatus.setSaving(false);
                            },
                        });
                    }}
                    disabled={savingStatus.isSaving}
                    sx={{ width: "100%" }}
                    startIcon={<FoodsIcon size={2} icon={"delete"} />}
                >
                    <Typography variant={"small-bold"} color={"inherit"}>
                        {t("action_plan.delete_action", "Delete action")}
                    </Typography>
                </Button>
            </Stack>
        </Box>
    );
};
